import { type AnchorHTMLAttributes, type ButtonHTMLAttributes, type HTMLAttributes, type ReactElement, type ReactNode } from 'react';
import { cx } from './library-kit';

type CardPad = 'none' | 'sm' | 'md' | 'lg';

interface CardBase {
  /** Inner padding: `none` · `sm` (12px) · `md` (16px, default) · `lg` (24px). */
  readonly padding?: CardPad;
  readonly className?: string;
  readonly children?: ReactNode;
}

/** A static card: a surface with a hairline border (a `section` by default). */
export interface StaticCardProps extends CardBase, Omit<HTMLAttributes<HTMLElement>, 'style' | 'className' | 'children'> {
  readonly as?: 'section' | 'div' | 'article' | 'li';
  readonly onClick?: undefined;
  readonly href?: undefined;
}
/** A card that is a button: the whole surface is the target. */
export interface ButtonCardProps extends CardBase, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'style' | 'className' | 'children' | 'type'> {
  readonly onClick: NonNullable<ButtonHTMLAttributes<HTMLButtonElement>['onClick']>;
  readonly href?: undefined;
  readonly as?: undefined;
}
/** A card that is a link. */
export interface LinkCardProps extends CardBase, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'style' | 'className' | 'children'> {
  readonly href: string;
  readonly onClick?: AnchorHTMLAttributes<HTMLAnchorElement>['onClick'];
  readonly as?: undefined;
}
export type CardProps = StaticCardProps | ButtonCardProps | LinkCardProps;

/**
 * The card (Design System FX-01): a white surface, 1px border, radius 12, padding 16. Pass `onClick` or `href` and it becomes the
 * INTERACTIVE card — a real `<button>`/`<a>` with the lift on hover (-2px, soft shadow, ink edge) and the press (scale .98), a
 * 48px minimum box and the theme focus ring. The lift is stilled under `prefers-reduced-motion` (it is an FX class), the
 * colour change on hover is not. Without them it is a plain section.
 */
export function Card(props: CardProps): ReactElement {
  const { padding = 'md', className, children } = props;
  const pad = padding === 'md' ? undefined : padding;
  if (props.href !== undefined) {
    const { padding: _p, className: _c, children: _ch, as: _as, ...anchor } = props;
    return (
      <a {...anchor} className={cx('vello-card', 'vello-card--interactive', className)} data-pad={pad}>
        {children}
      </a>
    );
  }
  if (props.onClick !== undefined) {
    const { padding: _p, className: _c, children: _ch, as: _as, ...button } = props;
    return (
      <button type="button" {...button} className={cx('vello-card', 'vello-card--interactive', className)} data-pad={pad}>
        {children}
      </button>
    );
  }
  const { padding: _p, className: _c, children: _ch, as: Tag = 'section', onClick: _o, href: _h, ...plain } = props;
  return (
    <Tag {...plain} className={cx('vello-card', className)} data-pad={pad}>
      {children}
    </Tag>
  );
}
